'use client';

import {
  applyPeriodSelection,
  PeriodPickerGroups,
  PickerPeriod,
  usePeriodGroups,
} from '@app/components/multi-period-picker';
import { Icons } from '@shared/components/content/icons';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { Separator } from '@shared/components/ui/separator';
import { cn } from '@shared/lib/utils';
import type { Column } from '@tanstack/react-table';
import * as React from 'react';
import { useFilterPillDensity } from './filter-pill-density';

interface DataTablePeriodFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  periods: PickerPeriod[];
}

/**
 * Time-period table filter — a slim, faceted-filter-styled trigger backed by
 * the grouped {@link PeriodPickerGroups} (months under their reporting year).
 * Mirrors `DataTableFacetedFilter`'s trigger so it sits in line with the other
 * filters, and summarises selections as "N selected" once more than two.
 */
export function DataTablePeriodFilter<TData, TValue>({
  column,
  title = 'Time period',
  periods,
}: DataTablePeriodFilterProps<TData, TValue>) {
  const [open, setOpen] = React.useState(false);
  const groups = usePeriodGroups(periods);
  const density = useFilterPillDensity();
  const maxNamed = density === 'compact' ? 1 : 2;

  const columnFilterValue = column?.getFilterValue();
  const selectedValues = React.useMemo(
    () =>
      Array.isArray(columnFilterValue) ? (columnFilterValue as string[]) : [],
    [columnFilterValue]
  );

  const labelByUuid = React.useMemo(
    () => new Map(periods.map((p) => [p.uuid, p.name])),
    [periods]
  );

  const setValues = React.useCallback(
    (values: string[]) =>
      column?.setFilterValue(values.length ? values : undefined),
    [column]
  );

  const onToggle = React.useCallback(
    (value: string) => {
      const next = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      setValues(applyPeriodSelection(next, selectedValues, periods));
    },
    [selectedValues, periods, setValues]
  );

  const onClear = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      setValues([]);
    },
    [setValues]
  );

  const selectedCount = selectedValues.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'border-dashed',
            density === 'compact' && 'min-w-0 max-w-full',
            selectedCount > 0 &&
              'border-primary-600 bg-primary-600 font-semibold text-gray-00 shadow-sm hover:bg-primary-600 hover:text-gray-00'
          )}
        >
          {selectedCount > 0 ? (
            <div
              role="button"
              aria-label={`Clear ${title} filter`}
              tabIndex={0}
              onClick={onClear}
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <Icons.xCircle className="mr-2 size-4" />
            </div>
          ) : (
            <Icons.plusCircle className="mr-2 size-4" />
          )}
          {title}
          {selectedCount > 0 && (
            <>
              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4"
              />
              <Badge
                variant="secondary"
                className="rounded-sm border-transparent bg-primary-500 px-1 font-normal text-gray-00 lg:hidden"
              >
                {selectedCount}
              </Badge>
              <div className="hidden min-w-0 items-center gap-1 lg:flex">
                {selectedCount > maxNamed ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm border-transparent bg-primary-500 px-1 font-normal text-gray-00"
                  >
                    {selectedCount} selected
                  </Badge>
                ) : (
                  selectedValues.map((value) => (
                    <Badge
                      variant="secondary"
                      key={value}
                      className="rounded-sm border-transparent bg-primary-500 px-1 font-normal text-gray-00"
                    >
                      {labelByUuid.get(value) ?? value}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-1" align="start">
        <PeriodPickerGroups
          groups={groups}
          selectedValues={selectedValues}
          onToggle={onToggle}
          onClear={() => setValues([])}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
